import { GeoJSON } from 'ol/format';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Circle, Fill, Stroke, Style } from 'ol/style';
import { MapLayer } from '../MapLayer';
/**
 * Vector layer for imported earthquake events. Renders each event as
 * a circle whose radius scales with magnitude and whose color shades
 * by depth (shallow = bright red, deep = washed-out brown). Click /
 * hover popovers come for free via the standard MapLayer pipeline.
 */
export class EarthquakeLayer extends MapLayer {
    constructor() {
        super(...arguments);
        /**
         * @protected
         */
        this._features = [];
        /**
         * Set of earthquake ids drawn with the thicker highlight stroke —
         * used by the impact view to make the focus event stand out from
         * neighbouring events in the same period filter.
         */
        this._highlightIds = new Set();
    }
    getName() {
        return 'earthquake_layer';
    }
    getTitle() {
        return 'Earthquakes';
    }
    getOlLayer() {
        if (!this._layer) {
            this._source = new VectorSource({ wrapX: false });
            this._layer = new VectorLayer({
                source: this._source,
                style: (feature) => EarthquakeLayer._styleFor(feature)
            });
            this._layer.setZIndex(94);
        }
        return this._layer;
    }
    /**
     * Mark a subset of earthquake ids as "focus" — drawn with a thick
     * dark outline + larger radius so they pop in the impact view.
     * Pass an empty list to clear.
     */
    setHighlight(ids) {
        this._highlightIds = new Set(ids);
        // Re-tag buffered features so the next refresh picks up the
        // new highlight state without the caller having to resupply
        // the whole quake list.
        for (const f of this._features) {
            f.properties.highlight = this._highlightIds.has(f.properties.id);
        }
    }
    /**
     * Replace the buffered features with the supplied earthquake list.
     * Followed by {@link refresh} to commit.
     */
    setEarthquakes(quakes) {
        this._features = quakes.map((q) => {
            var _a;
            return ({
                type: 'Feature',
                properties: {
                    pointtype: 'earthquake',
                    id: q.id,
                    magnitude: q.magnitude,
                    depth_km: (_a = q.depth_km) !== null && _a !== void 0 ? _a : 0,
                    event_time_ms: q.event_time_ms,
                    place: q.place,
                    magnitude_type: q.magnitude_type,
                    url: q.url,
                    highlight: this._highlightIds.has(q.id)
                },
                geometry: {
                    type: 'Point',
                    coordinates: [q.lon, q.lat]
                }
            });
        });
    }
    refresh() {
        if (!this._source) {
            return;
        }
        this._source.clear(true);
        if (this._features.length === 0) {
            return;
        }
        const collection = {
            type: 'FeatureCollection',
            crs: { type: 'name', properties: { name: 'EPSG:4326' } },
            features: this._features
        };
        const olFeatures = new GeoJSON().readFeatures(collection, { featureProjection: 'EPSG:3857' });
        this._source.addFeatures(olFeatures);
    }
    /**
     * Magnitude → circle radius (px). Linear within the usual 2.5–7
     * range; clamped at the edges so a giant M 8 doesn't paint half
     * the screen.
     */
    static _radiusFor(mag) {
        const clamped = Math.max(2, Math.min(8, mag));
        return 3 + (clamped - 2) * 2.5;
    }
    /**
     * Depth → fill color. Shallow events sit at the strongest red;
     * deeper events fade toward a muted ochre. Helps the eye pick out
     * the shallow ones (which are the ones whales would actually feel).
     */
    static _fillFor(depthKm) {
        if (!Number.isFinite(depthKm)) {
            return 'rgba(220, 53, 69, 0.7)';
        }
        if (depthKm < 30) {
            return 'rgba(220, 53, 69, 0.75)';
        }
        if (depthKm < 100) {
            return 'rgba(253, 126, 20, 0.65)';
        }
        return 'rgba(133, 100, 60, 0.55)';
    }
    static _styleFor(feature) {
        const props = feature.getProperties() || {};
        if (props.pointtype !== 'earthquake') {
            return [];
        }
        const mag = Number(props.magnitude) || 0;
        const depth = Number(props.depth_km) || 0;
        const highlight = props.highlight === true;
        return [new Style({
                image: new Circle({
                    radius: EarthquakeLayer._radiusFor(mag) + (highlight ? 3 : 0),
                    fill: new Fill({ color: EarthquakeLayer._fillFor(depth) }),
                    stroke: new Stroke({
                        color: highlight ? 'rgba(0,0,0,0.95)' : 'rgba(0,0,0,0.65)',
                        width: highlight ? 3 : 1
                    })
                })
            })];
    }
}
